/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { X, CheckCircle2, XCircle, Loader2, Video, FileText, AlertTriangle } from "lucide-react";
import { ConsentScreen } from "./ConsentScreen";
import { TelesoinRoom } from "./TelesoinRoom";
import { ActivityGauge } from "./ActivityGauge";

export const telesoinError = (e) => {
  const d = e.response?.data?.detail;
  if (d && typeof d === "object" && !Array.isArray(d) && d.message) return `${d.message}${d.controles?.length ? " : " + d.controles.map((c) => c.detail).join(" ") : ""}`;
  return formatApiErrorDetail(d);
};

function Checklist({ elig }) {
  return (
    <ul data-testid="telesoin-checklist" className="flex flex-col gap-1.5">
      {elig.controles.map((c) => (
        <li key={c.code} data-testid={`telesoin-check-${c.code}`} className={`flex items-start gap-2 rounded-2xl px-3 py-2 text-[13px] ${c.ok ? "bg-[#5CB85C]/10 text-[#2d7a2d]" : "bg-[#D9534F]/10 text-[#b23a36]"}`}>
          {c.ok ? <CheckCircle2 size={16} className="mt-0.5 shrink-0" /> : <XCircle size={16} className="mt-0.5 shrink-0" />}
          <div><b>{c.libelle}</b>{c.derogation && <span className="ml-1 text-[11px] font-bold text-[#8A6D2B]">(dérogation)</span>}<div className="text-[12px] opacity-80">{c.detail}</div></div>
        </li>
      ))}
    </ul>
  );
}

function PlanForm({ patientId, onCreated }) {
  const [nomen, setNomen] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [acte, setActe] = useState("");
  const [urgence, setUrgence] = useState(false);
  const [lien, setLien] = useState("");
  const [elig, setElig] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/telesoin/nomenclature").then(({ data }) => { setNomen(data); setActe((a) => a || (data.actes.find((x) => !x.bilan || !data.regles.includes("bilan")) || {}).code || ""); }).catch(() => null);
    api.get("/telesoin/config").then(({ data }) => setCfg(data)).catch(() => null);
  }, []);
  const check = useCallback(() => acte && api.get(`/telesoin/eligibilite?patient_id=${patientId}&acte_code=${acte}&urgence=${urgence}`).then(({ data }) => setElig(data)).catch(() => null), [patientId, acte, urgence]);
  useEffect(() => { check(); }, [check]);
  const create = async () => {
    setBusy(true);
    try { const { data } = await api.post("/telesoin/seances", { patient_id: patientId, acte_code: acte, urgence_sortie_hospitalisation: urgence, lien_visio: lien || null }); toast.success("Séance de télésoin planifiée"); onCreated(data); }
    catch (e) { toast.error(telesoinError(e)); check(); }
    setBusy(false);
  };
  const connu = elig?.controles.find((c) => c.code === "patient_connu");
  const needLink = cfg?.fournisseur && cfg.fournisseur !== "hds";
  return (
    <div className="flex flex-col gap-3">
      {elig?.compteur.applicable && <ActivityGauge c={elig.compteur} compact />}
      {elig ? <Checklist elig={elig} /> : <Loader2 className="animate-spin text-[#2BA3A0]" />}
      <div>
        <label className="text-[11px] font-bold text-[#0A1426]/60 ml-3">{nomen?.regles.includes("bilan") ? "Acte (séances de suivi uniquement — bilans exclus)" : `Acte (${nomen?.regime_nom || "…"})`}</label>
        <select data-testid="telesoin-acte-select" value={acte} onChange={(e) => setActe(e.target.value)} className="h-11 w-full px-4 rounded-full border border-[#E4E0D5] text-sm text-[#0A1426] bg-white">
          {(nomen?.actes || []).map((a) => { const interdit = a.bilan && nomen.regles.includes("bilan"); return <option key={a.code} value={a.code} disabled={interdit}>{`${a.libelle} — ${interdit ? "interdit en télésoin" : `${nomen.lettre_cle_distance} ${a.coefficient} · ${a.montant.toFixed(2)} ${nomen.devise}`}`}</option>; })}
        </select>
      </div>
      {connu && !connu.ok && (
        <label data-testid="telesoin-urgence-label" className="flex items-start gap-3 rounded-2xl border-2 border-[#D4AF5A] bg-[#D4AF5A]/10 p-3 text-[13px] text-[#0A1426] cursor-pointer">
          <input data-testid="telesoin-urgence-checkbox" type="checkbox" checked={urgence} onChange={(e) => setUrgence(e.target.checked)} className="mt-0.5 w-5 h-5 accent-[#D4AF5A]" />
          <span><b>Sortie d'hospitalisation (urgence)</b> — je déclare explicitement cette dérogation au contrôle « patient connu ». Elle sera tracée dans le compte rendu.</span>
        </label>
      )}
      {needLink && (
        <div>
          <label className="text-[11px] font-bold text-[#0A1426]/60 ml-3">Lien de la salle {cfg.fournisseur === "doctolib" ? "Doctolib" : "Maiia"} (https)</label>
          <input data-testid="telesoin-link-input" value={lien} onChange={(e) => setLien(e.target.value)} placeholder={`https://www.${cfg.fournisseur === "doctolib" ? "doctolib.fr" : "maiia.com"}/…`} className="h-11 w-full px-4 rounded-full border border-[#E4E0D5] text-sm text-[#0A1426]" />
        </div>
      )}
      {cfg && !cfg.fournisseur && <div data-testid="telesoin-no-provider" className="text-[12px] text-[#b23a36] flex items-center gap-1"><AlertTriangle size={14} /> Configurez un fournisseur vidéo conforme dans Télésoin › Réglages.</div>}
      <Button data-testid="telesoin-plan-btn" disabled={busy || !elig?.ok || (needLink && !lien)} onClick={create} className="h-12 rounded-full bg-[#0A1426] text-white font-bold">
        {busy ? <Loader2 className="animate-spin" size={16} /> : elig?.ok ? "Planifier la séance de télésoin" : "Télésoin non autorisé"}
      </Button>
    </div>
  );
}

function Report({ result, onClose }) {
  const cr = result.compte_rendu;
  return (
    <div data-testid="telesoin-report" className="flex flex-col gap-3">
      <div className="flex items-center gap-2 text-[#2d7a2d]"><CheckCircle2 size={20} /><span className="font-[Fraunces] text-lg font-bold">Séance terminée · acte {result.acte.lettre_cle} {result.acte.coefficient} codé ({Number(result.acte.montant).toFixed(2)} {result.acte.devise})</span></div>
      <div className="rounded-2xl bg-[#F5F3EE] p-3 text-[13px] text-[#0A1426] flex flex-col gap-1">
        <div><b>Compte rendu</b> archivé · {cr.duree_min} min · {cr.fournisseur}</div>
        <div>Tension {cr.donnees.tension || "—"} · Humeur {cr.donnees.humeur || "—"} · Séances 7 j : {cr.donnees.seances_7j} · Observance {cr.donnees.observance_7j_pct ?? "—"} %</div>
        {cr.observations && <div>Observations : {cr.observations}</div>}
        {result.facture && <div data-testid="telesoin-report-facture" className="font-bold">Facture patient {result.facture.numero} émise ({Number(result.facture.montant).toFixed(2)} {result.facture.devise}) — envoi depuis la fiche patient.</div>}
        {result.compte_rendu.acte.lettre_cle === "TMK" && <div className="text-[11px] text-[#0A1426]/60">Ajouté à la file DMP / MSSanté (envoi SIMULÉ — connecteur non raccordé).</div>}
      </div>
      {result.compteur.applicable && <ActivityGauge c={result.compteur} compact />}
      <div className="flex gap-2">
        <a data-testid="telesoin-report-pdf" href={`${api.defaults.baseURL}/telesoin/comptes-rendus/${cr.id}/pdf`} onClick={async (e) => { e.preventDefault(); const r = await api.get(`/telesoin/comptes-rendus/${cr.id}/pdf`, { responseType: "blob" }); window.open(URL.createObjectURL(r.data), "_blank"); }}
          className="flex-1 h-11 rounded-full border border-[#0A1426] text-[#0A1426] font-bold inline-flex items-center justify-center gap-2 text-sm"><FileText size={16} /> PDF</a>
        <Button data-testid="telesoin-report-close" onClick={onClose} className="flex-1 h-11 rounded-full bg-[#2BA3A0] text-white font-bold">Fermer</Button>
      </div>
    </div>
  );
}

export function TelesoinPanel({ patientId, onClose, onChanged }) {
  const [seance, setSeance] = useState(undefined);
  const [session, setSession] = useState(null);
  const [result, setResult] = useState(null);
  const load = useCallback(() => api.get(`/telesoin/seances?patient_id=${patientId}`).then(({ data }) => setSeance(data.find((s) => ["planifie", "en_cours"].includes(s.statut)) || null)).catch(() => setSeance(null)), [patientId]);
  useEffect(() => { load(); }, [load]);
  const start = async () => {
    try { const { data } = await api.post(`/telesoin/seances/${seance.id}/demarrer`); setSession(data); }
    catch (e) { toast.error(telesoinError(e)); }
  };
  const end = async (body) => {
    try { const { data } = await api.post(`/telesoin/seances/${seance.id}/terminer`, body); setSession(null); setResult(data); setSeance(null); onChanged?.(); }
    catch (e) { toast.error(telesoinError(e)); }
  };
  const cancel = async () => { await api.post(`/telesoin/seances/${seance.id}/annuler`); toast.success("Séance annulée"); load(); onChanged?.(); };
  if (session) return <TelesoinRoom seance={seance} session={session} pro onEnd={end} onClose={() => setSession(null)} />;
  return (
    <div data-testid="telesoin-panel" className="fixed inset-0 z-[90] bg-[#0A1426]/60 backdrop-blur-sm grid place-items-center p-3" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-lg max-h-[92vh] overflow-y-auto p-5 flex flex-col gap-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-[#0A1426]"><Video size={18} className="text-[#2BA3A0]" /><h2 className="font-[Fraunces] text-xl font-bold">Séance vidéo · Télésoin</h2></div>
          <button data-testid="telesoin-panel-close" onClick={onClose} className="w-10 h-10 grid place-items-center rounded-full bg-[#F5F3EE] text-[#0A1426]"><X size={18} /></button>
        </div>
        {result ? <Report result={result} onClose={onClose} /> : seance === undefined ? <Loader2 className="animate-spin text-[#2BA3A0]" /> : seance === null ? <PlanForm patientId={patientId} onCreated={(s) => { setSeance(s); onChanged?.(); }} /> : (
          <div className="flex flex-col gap-3">
            <div data-testid="telesoin-seance-info" className="rounded-2xl bg-[#F5F3EE] p-3 text-[13px] text-[#0A1426]">
              <div className="font-bold">{seance.acte_libelle} · {seance.lettre_cle} {seance.coefficient} · {Number(seance.montant ?? seance.montant_eur).toFixed(2)} {seance.devise || "€"}</div>
              <div className="text-[12px] text-[#0A1426]/70">{new Date(seance.datetime).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" })} · {seance.fournisseur === "hds" ? "Visio HDS" : seance.fournisseur} · statut <b>{seance.statut}</b>{seance.urgence_sortie_hospitalisation ? " · dérogation sortie d'hospitalisation" : ""}</div>
            </div>
            <Checklist elig={{ controles: seance.controles }} />
            <ConsentScreen seance={seance} compact onDone={(c) => setSeance({ ...seance, consentement: c })} />
            <div className="flex gap-2">
              <Button data-testid="telesoin-cancel-btn" variant="outline" onClick={cancel} className="h-12 rounded-full border-[#D9534F] text-[#b23a36] font-bold">Annuler</Button>
              <Button data-testid="telesoin-start-btn" disabled={!seance.consentement} onClick={start} className="flex-1 h-12 rounded-full bg-[#2BA3A0] text-white font-bold"><Video size={16} className="mr-2" /> {seance.statut === "en_cours" ? "Reprendre la séance" : "Démarrer la vidéo sécurisée"}</Button>
            </div>
          </div>
        )}
        <p className="text-[10px] text-[#0A1426]/50">Outil d'aide à l'organisation et à la traçabilité — n'est pas un dispositif médical. Règles et nomenclature selon le pays d'exercice.</p>
      </div>
    </div>
  );
}
